import React from 'react';

import { Box, Grid, Typography } from '@mui/material';

import { CORPORATE_STRUCTURE_OPTIONS, INDUSTRIES_OPTIONS } from '@clearverify/constants';

import { DEFAULT_AVATAR } from 'src/assets';
import { Button } from 'src/components/common';
import {
  AddressFields,
  AutocompleteField,
  AvatarImageUploadField,
  Field,
  Form,
  MaskedTextField,
  NumberField,
  PhoneField,
  SelectField,
  TextField,
} from 'src/components/finalForm';
import { TAX_YEAR_END_DATE_MASK } from 'src/constants/masks';
import { useAuthSignup } from 'src/hooks/auth';
import type { SxProp } from 'src/types/mui';
import { getUserFullName } from 'src/utils/user';
import { validateWithYupSchema } from 'src/utils/yup';

import { EmployerBasicInfoFormFields, EmployerBasicInfoFormValues } from '../types';
import { employerBasicInfoFormValidationSchema } from '../utils';

export const EmployerBasicInfoForm: React.FC = () => {
  const { formData, inviteData, updateFormData, goToNextStep } = useAuthSignup();

  const handleSubmit = React.useCallback(
    (formValues: EmployerBasicInfoFormValues) => {
      updateFormData(formValues);
      goToNextStep();
    },
    [goToNextStep, updateFormData],
  );

  return (
    <Box>
      <Typography variant="h5">Basic info</Typography>
      <Typography variant="body1" mt={1}>
        Hello <b>{getUserFullName(formData)}</b>! Let us know more information about your company.
      </Typography>
      <Form<EmployerBasicInfoFormValues>
        onSubmit={handleSubmit}
        subscription={{ valid: true, submitting: true }}
        containerProps={{ mt: 4 }}
        validate={validateWithYupSchema(employerBasicInfoFormValidationSchema)}
      >
        {({ submitting, valid }) => {
          return (
            <React.Fragment>
              <Grid container spacing={1.5}>
                <Grid
                  size={{
                    xs: 12,
                    md: 8,
                  }}
                >
                  <Box>
                    <Field
                      component={TextField}
                      name={EmployerBasicInfoFormFields.displayName}
                      label="Company Display Name"
                      fullWidth
                      disabled={submitting}
                      required
                    />
                  </Box>

                  <Box mt={1.5}>
                    <Field
                      label="Corporate Structure"
                      name={EmployerBasicInfoFormFields.corporateStructure}
                      fullWidth
                      disabled={submitting}
                      options={CORPORATE_STRUCTURE_OPTIONS}
                      component={SelectField}
                    />
                  </Box>
                </Grid>

                <Grid
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  size={{
                    xs: 12,
                    md: 4,
                  }}
                >
                  <Box sx={avatarStyles}>
                    <Field
                      name={EmployerBasicInfoFormFields.avatarImageFile}
                      component={AvatarImageUploadField}
                      defaultImageUrl={DEFAULT_AVATAR}
                      avatarProps={{ sx: avatarStyles }}
                      uploadInfoQueryVariables={{ inviteId: inviteData?.id }}
                      disabled={submitting}
                    />
                  </Box>
                </Grid>

                <Grid size={12}>
                  <Field
                    component={AutocompleteField}
                    name={EmployerBasicInfoFormFields.industry}
                    label="Industry"
                    options={INDUSTRIES_OPTIONS}
                    fullWidth
                    disabled={submitting}
                  />
                </Grid>

                <Grid size={6}>
                  <Field
                    fullWidth
                    label="Tax Year End Date"
                    name={EmployerBasicInfoFormFields.taxYearEndDate}
                    component={MaskedTextField}
                    inputProps={{
                      placeholder: 'MM/DD',
                    }}
                    mask={TAX_YEAR_END_DATE_MASK}
                    disabled={submitting}
                  />
                </Grid>

                <Grid size={6}>
                  <Field
                    label="Number of Employees"
                    name={EmployerBasicInfoFormFields.numberOfEmployees}
                    fullWidth
                    disabled={submitting}
                    component={NumberField}
                  />
                </Grid>

                <Grid size={12}>
                  <Field
                    component={TextField}
                    name={EmployerBasicInfoFormFields.contactEmail}
                    label="Primary Contact Email"
                    fullWidth
                    disabled={submitting}
                    required
                  />
                </Grid>

                <Grid size={12}>
                  <Field
                    component={PhoneField}
                    name={EmployerBasicInfoFormFields.contactPhoneNumber}
                    label="Primary Contact Phone"
                    fullWidth
                    disabled={submitting}
                  />
                </Grid>

                <Grid spacing={2}>
                  <Grid size={12}>
                    <Typography fontWeight={600}>Headquarters address</Typography>
                  </Grid>
                  <Grid mt={2}>
                    <AddressFields
                      prefix={EmployerBasicInfoFormFields.headquarters}
                      requiredStreet
                      requiredCity
                      requiredState
                      requiredZipCode
                      disabled={submitting}
                      addressLabel="Street"
                    />
                  </Grid>
                </Grid>
                {!inviteData?.isEmployerUnderPeo && (
                  <Grid size={12}>
                    <Grid size={12}>
                      <Typography fontWeight={600}>Employer Disclaimer</Typography>
                    </Grid>
                    <Grid mt={2} size={12}>
                      <Field
                        name={EmployerBasicInfoFormFields.disclaimer}
                        label="Disclaimer"
                        disabled={submitting}
                        component={TextField}
                        multiline
                        fullWidth
                        minRows={3}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
              <Box display="flex" justifyContent="center" mt={4}>
                <Button
                  type="submit"
                  sx={submitButtonStyles}
                  disabled={submitting || !valid}
                  loading={submitting}
                  size="large"
                  variant="contained"
                >
                  Next step
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </Box>
  );
};

const submitButtonStyles: SxProp = {
  px: 5,
};

const avatarStyles: SxProp = {
  width: 120,
  height: 120,
};
